// src/components/ClientSearch.js
import React, { useState } from 'react';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../services/firebase';
import { Search, X, User, Mail, Phone } from 'lucide-react';

const ClientSearch = ({ onSelectClient, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const handleSearch = async () => {
    if (!searchTerm || searchTerm.length < 2) return;
    
    setSearching(true);
    try {
      const searchLower = searchTerm.toLowerCase();
      
      // Récupérer tous les clients et filtrer côté client
      // (Firestore ne supporte pas les recherches "contains" natives)
      const clientsRef = collection(db, 'clients');
      const snapshot = await getDocs(query(clientsRef, orderBy('name')));
      
      const results = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        const fullName = `${data.firstName || ''} ${data.name || ''}`.toLowerCase();
        const email = (data.email || '').toLowerCase();
        const phone = (data.phone || '').toLowerCase();
        
        if (fullName.includes(searchLower) || 
            email.includes(searchLower) || 
            phone.includes(searchLower)) {
          results.push({
            id: doc.id,
            ...data
          });
        }
      });
      
      setSearchResults(results.slice(0, 10)); // Limiter à 10 résultats
    } catch (error) {
      console.error('Erreur recherche client:', error);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Recherche de client</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded">
              <X size={20} />
            </button>
          </div>
          
          {/* Barre de recherche */}
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Nom, prénom, email ou téléphone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                autoFocus
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={searching || searchTerm.length < 2}
              className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
            >
              {searching ? 'Recherche...' : 'Recherche'}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Recherche de détails sur les clients
          </p>
        </div>
        
        {/* Résultats */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-200px)]">
          {searchResults.length > 0 && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">
                  ℹ️ Nous avons trouvé plusieurs correspondances. Voici les meilleurs résultats.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {searchResults.map(client => (
                  <div key={client.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-lg">
                          {client.firstName} {client.name}
                        </h3>
                        {client.isReturningClient && (
                          <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded mt-1">
                            Client qui revient
                          </span>
                        )}
                        {client.isClubMember && (
                          <span className="inline-block bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded mt-1 ml-1">
                            Club
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-sm text-gray-600 mb-4">
                      <div className="flex items-start gap-2">
                        <User size={14} className="mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium">Adresse</p>
                          <p>{client.address || 'Non renseignée'}</p>
                          {client.city && client.postalCode && (
                            <p>{client.postalCode} {client.city}</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Phone size={14} className="flex-shrink-0" />
                        <div>
                          <p className="font-medium">Numéro de téléphone</p>
                          <p>{client.phone || 'Non renseigné'}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Mail size={14} className="flex-shrink-0" />
                        <div>
                          <p className="font-medium">Courriel</p>
                          <p className="break-all">{client.email || 'Non renseigné'}</p>
                        </div>
                      </div>
                      
                      {client.lastReservationDate && (
                        <div className="text-xs text-gray-500 mt-2">
                          Dernière réservation: {new Date(client.lastReservationDate.seconds * 1000).toLocaleDateString('fr-FR')}
                        </div>
                      )}
                    </div>
                    
                    <button
                      onClick={() => onSelectClient(client)}
                      className="w-full py-2 border border-gray-300 rounded-lg hover:bg-gray-100 flex items-center justify-center gap-2"
                    >
                      Sélectionner le client →
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
          
          {searchTerm.length >= 2 && !searching && searchResults.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Search className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>Aucun client trouvé</p>
            </div>
          )}
          
{searchTerm.length >= 2 && !searching && searchResults.length === 0 ? (
  <div className="text-center py-8 text-gray-500">
    <Search className="w-12 h-12 mx-auto mb-2 opacity-30" />
    <p>Aucun client trouvé</p>
  </div>
) : searchTerm.length < 2 ? (
  <div className="text-center py-8 text-gray-400">
    <p className="text-sm">Saisissez au moins 2 caractères et cliquez sur "Recherche"</p>
  </div>
) : null}
        </div>
      </div>
    </div>
  );
};

export default ClientSearch;